import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  SxProps,
  Theme,
  styled,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { Text } from '@components/Helpers'
import { useCallback, useEffect, useState } from 'react'
import SelectIcon from '@assets/SelectIcon'

const SelectField = styled(Select)(() => ({
  // A filled field, like the search box: no outline in any state.
  backgroundColor: 'var(--dewi_colors_dark_7)',
  borderRadius: 'var(--dewi_radius_rounded)',
  height: 43,
  '.MuiOutlinedInput-notchedOutline': {
    border: 'none !important',
  },
  border: 'none !important',
  '.MuiSelect-icon': {
    color: 'var(--dewi_colors_gray_2)',
  },
}))

const SelectLabel = styled(InputLabel)({
  fontFamily: 'var(--font-nunito), sans-serif',
  color: 'white',
  fontWeight: 'normal',
})

const SelectItem = styled(MenuItem)({})

type SelectInputProp = {
  onChange: (value: any) => void
  items: { name: string | JSX.Element; value: string }[] | string[]
  noEmptyValue?: boolean
  initialValue: any
  noLabel?: boolean
  sx?: Partial<{
    menuItem: SxProps<Theme>
    formControl: SxProps<Theme>
    label: SxProps<Theme>
    input: SxProps<Theme>
    itemText: SxProps<Theme>
  }>
}

export default function SelectInput({
  onChange,
  items,
  noEmptyValue,
  noLabel,
  initialValue,
  sx,
}: SelectInputProp) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)

  const onOpen = useCallback(() => setOpen(true), [setOpen])
  const onClose = useCallback(() => setOpen(false), [setOpen])

  const [value, setValue] = useState(initialValue)

  const changeHandler = useCallback(
    (event: SelectChangeEvent<unknown>) => {
      const newValue = event.target.value as string
      onChange(newValue)
      setValue(newValue)
    },
    [onChange],
  )

  useEffect(() => {
    setValue(initialValue)
  }, [initialValue])

  return (
    <FormControl sx={sx?.formControl} fullWidth>
      {!noLabel && (
        <SelectLabel sx={sx?.label}>
          {t('explorerPage.overview.form.select')}
        </SelectLabel>
      )}
      <SelectField
        IconComponent={SelectIcon}
        onOpen={onOpen}
        onClose={onClose}
        sx={sx?.input}
        onChange={changeHandler}
        value={value}
        MenuProps={{
          disableScrollLock: true,
          sx: {
            '.MuiPaper-root': {
              maxHeight: 'var(--dewi_spacing_96)',
              background: 'var(--dewi_surface_card) !important',
              borderRadius: 'var(--dewi_radius_lg)',
              '.MuiButtonBase-root': {
                border: 'none !important',
              },
              // The selected row is a filled row, not an outlined one.
              '.MuiButtonBase-root:hover': {
                backgroundColor: 'var(--dewi_colors_dark_6)',
              },
              '.Mui-selected': {
                backgroundColor: 'var(--dewi_colors_dark_5) !important',
              },
            },
          },
        }}
      >
        {!noEmptyValue && (
          <SelectItem sx={sx?.menuItem} value="">
            <Text
              sx={{
                color: open
                  ? 'var(--dewi_colors_gray_0)'
                  : 'var(--dewi_colors_gray_2)',
                fontFamily: 'var(--font-nunito), sans-serif',
                ...sx?.itemText,
              }}
            >
              {t('explorerPage.overview.form.emptyValue')}
            </Text>
          </SelectItem>
        )}
        {items &&
          items.map((item) => (
            <SelectItem
              sx={sx?.menuItem}
              key={typeof item === 'object' ? item?.value : item}
              value={typeof item === 'object' ? item?.value : item}
            >
              <Text
                sx={{
                  color: open
                    ? 'var(--dewi_colors_gray_0)'
                    : 'var(--dewi_colors_gray_2)',
                  fontFamily: 'var(--font-nunito), sans-serif',
                  ...sx?.itemText,
                }}
              >
                {typeof item === 'object' ? item.name : item}
              </Text>
            </SelectItem>
          ))}
      </SelectField>
    </FormControl>
  )
}
